import { array, number, object, string } from "yup";
import { Button, Flex, OverlayStyle, SlideoutDrawerV2 } from "@jsq/ds/core";
import { Form, SubmitButton } from "@jsq/ds/forms";
import { Colors } from "@jsq/ds/styles";
import { SPACING } from "~/styles/variables";
import { DataFormatSection } from "../../../../shared/DataFormatSection";
import { RulesSection } from "../../../../shared/RulesSection";
import { rulesSchema } from "../../../../shared/RulesSection/validation";
import { supportsTransactionTypes } from "../../../../shared/TransactionTypesField/supportedTemplateTypes";
import { FocusFirstFormErrorOnSubmit } from "../../../focusFirstFormError";
import { NAME_MAX_LENGTH } from "./constants";
import { GeneralCard } from "./GeneralCard";
import * as styles from "./styles_vanilla.css";
import { Z_INDEX } from "./zIndex";
import type { QuerySettingsValues } from "./types";
import type { AttributeLeaf } from "../../../../shared/RulesSection/attributeCatalogue";
import type { TypeHierarchy } from "../../HierarchyTreeGrid";

type QuerySettingsPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  initialValues: QuerySettingsValues;
  nameError?: string | null;
  /** Parent must close via `onClose` after the update completes (or after
   * buffering the values, in new-query mode). */
  onSave: (values: QuerySettingsValues) => Promise<void>;
  /** Disables all controls and hides the Update button. */
  isReadOnly?: boolean;
  arenaId?: number | null;
  attributeLeaves?: ReadonlyArray<AttributeLeaf>;
  /** Type hierarchy blob from `finpubDataPackage.typeHierarchy` — feeds the
   *  transaction-type node picker in `GeneralCard`. */
  typeHierarchy?: TypeHierarchy | null;
};

const validationSchema = object({
  name: string()
    .required("Query name is required")
    .trim()
    .max(NAME_MAX_LENGTH, `Maximum ${NAME_MAX_LENGTH} characters`),
  supportedTransactionTypes: array()
    .of(number().required())
    .when("supportedTemplateType", {
      is: supportsTransactionTypes,
      then: (schema) => schema.min(1, "Select at least one transaction type"),
    })
    .required(),
  filterRules: rulesSchema,
  suppressionRules: rulesSchema,
});

export const QuerySettingsPanel = ({
  isOpen,
  onClose,
  initialValues,
  nameError = null,
  onSave,
  isReadOnly = false,
  arenaId = null,
  attributeLeaves,
  typeHierarchy = null,
}: QuerySettingsPanelProps) => {
  return (
    <SlideoutDrawerV2
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={false}
      overlayStyle={OverlayStyle.Transparent}
      zIndex={Z_INDEX.drawer}
      data-testid="query-settings-panel"
    >
      <Flex
        flexDirection="column"
        className={styles.drawerWidth}
        data-testid="query-settings-form"
      >
        <Form<QuerySettingsValues>
          key={nameError ?? "no-name-error"}
          initialValues={initialValues}
          initialErrors={nameError ? { name: nameError } : undefined}
          initialTouched={nameError ? { name: true } : undefined}
          validationSchema={validationSchema}
          onSubmit={onSave}
          submitOnlyChangedValues={false}
          onSubmitErrorMessage="Something went wrong saving these settings"
          enableReinitialize
        >
          <FocusFirstFormErrorOnSubmit />
          <Flex flexDirection="column" className={styles.form}>
            <SlideoutDrawerV2.ComplexHeader
              className={styles.header}
              title="Query settings"
              description="Configure the name, default display format, and filter and suppression rules for this query."
              closeButton={
                <Button
                  variant="custom"
                  textColor={Colors.Neutral700}
                  backgroundColor={Colors.Neutral0}
                  icon="xmark"
                  iconPosition="right"
                  data-testid="query-settings-close-btn"
                >
                  {isReadOnly ? "Close" : "Close without saving"}
                </Button>
              }
            />
            <Flex flexDirection="column" gap={SPACING.LARGE} className={styles.body}>
              <GeneralCard isReadOnly={isReadOnly} typeHierarchy={typeHierarchy} />
              <DataFormatSection
                toggleLabel="Format within query"
                toggleName="formatWithinQuery"
                valuesPath=""
                variant="query"
                isReadOnly={isReadOnly}
              />
              {(!isReadOnly || initialValues.filterRules.groups.length > 0) && (
                <RulesSection
                  title="Filter rules"
                  description="Only generate reports when they meet these filter criteria"
                  name="filterRules"
                  isReadOnly={isReadOnly}
                  arenaId={arenaId}
                  attributeLeaves={attributeLeaves}
                />
              )}
              {(!isReadOnly || initialValues.suppressionRules.groups.length > 0) && (
                <RulesSection
                  title="Suppression rules"
                  description="Do not generate reports when they meet these criteria"
                  name="suppressionRules"
                  isReadOnly={isReadOnly}
                  arenaId={arenaId}
                  attributeLeaves={attributeLeaves}
                />
              )}
            </Flex>
            {!isReadOnly && (
              <Flex alignItems="center" justifyContent="flex-end" className={styles.footer}>
                <SubmitButton data-testid="save-query-settings-btn">Save settings</SubmitButton>
              </Flex>
            )}
          </Flex>
        </Form>
      </Flex>
    </SlideoutDrawerV2>
  );
};
